import { CONTENT as DOCUMENT_CONTENT } from "@nib-group/chatbot-shared/document-title";
import { CONFIG as LAUNCH_CONFIG } from "@nib-group/chatbot-shared/launch-utils";
import { MSG_KEYS, sendPostMessage, listenForPostMessage } from "@nib-group/chatbot-shared/post-message";
// ----------------------------------------
// Constants
export const CONTENT = {
  ERRORS: {
    DOMAIN_NOT_ALLOWED: "Event received from domain that is not allowed:"
  }
};
export const CONFIG = {
  DEFAULT_PROTOCOL: "https:",
  DEFAULT_ORIGIN: "www.nib.com.au",
  WINDOW: {
    HEIGHT: 645,
    WIDTH: 465,
    INSET_TOP: 130,
    INSET_LEFT: 20
  }
};
export const WINDOW_OPTIONS = (screen, options) => {
  var _a, _b, _c, _d;
  return ["resizable", `height=${(_a = options === null || options === void 0 ? void 0 : options.height) !== null && _a !== void 0 ? _a : CONFIG.WINDOW.HEIGHT}`, `width=${(_b = options === null || options === void 0 ? void 0 : options.width) !== null && _b !== void 0 ? _b : CONFIG.WINDOW.WIDTH}`, `top=${(_c = options === null || options === void 0 ? void 0 : options.top) !== null && _c !== void 0 ? _c : screen.width - CONFIG.WINDOW.HEIGHT - CONFIG.WINDOW.INSET_TOP}`, `left=${(_d = options === null || options === void 0 ? void 0 : options.left) !== null && _d !== void 0 ? _d : screen.width - CONFIG.WINDOW.HEIGHT - CONFIG.WINDOW.INSET_LEFT}`].join(", ");
};
// ----------------------------------------
/**
 * The URL of the chat window is determined by the launcher's window
 * protocol and hostname, plus path. A consideration is also
 * made against `localhost` to streamline local development.
 */
export const determineLaunchUrl = (relativePath, fullPathOverride) => {
  var _a, _b;
  // A dedicated query parameter tells the `chatbot-client` to
  // defer initialisation until it loads an external config
  const queryParam = `?${LAUNCH_CONFIG.TRANSMIT_CONFIG_QUERY_PARAM}`;
  // Determine chatbot launch URL
  const protocol = (_a = window.location.protocol) !== null && _a !== void 0 ? _a : CONFIG.DEFAULT_PROTOCOL;
  const hostname = (_b = window.location.hostname) !== null && _b !== void 0 ? _b : CONFIG.DEFAULT_ORIGIN;
  const targetOrigin = `${protocol}//${hostname}`;
  const targetUrl = fullPathOverride !== null && fullPathOverride !== void 0 ? fullPathOverride : `${targetOrigin}${relativePath}`;
  return `${targetUrl}${queryParam}`;
};
/**
 * A launch config can only be received by the chatbot `client` AFTER it has
 * initially loaded. This intent is flagged when the `client` window is opened
 * via a query parameter. If present, the `client` window will post back a message
 * when it has finished its initial load, whereupon we can send through the config
 * object and the `client` can init the chatbot with the provided config.
 */
export const waitForClientToLoad = _ref => {
  let {
    initialLoadCallback,
    readyToRenderCallback
  } = _ref;
  listenForPostMessage({
    msgKey: MSG_KEYS.CLIENT_HAS_LOADED,
    onlyOnce: true,
    onSuccess: initialLoadCallback,
    onError: event => {
      console.warn(CONTENT.ERRORS.DOMAIN_NOT_ALLOWED, event.origin);
    }
  });
  // If an external config is being loaded, we can listen for
  // a separate postMessage event that will indicate to us when
  // the config has been successfully loaded.
  // (This distinction is necessary to load things like the
  // config-driven theme before we can display any client UI)
  if (readyToRenderCallback) {
    listenForPostMessage({
      msgKey: MSG_KEYS.CLIENT_READY_TO_RENDER,
      onlyOnce: true,
      onSuccess: readyToRenderCallback,
      onError: event => {
        console.warn(CONTENT.ERRORS.DOMAIN_NOT_ALLOWED, event.origin);
      }
    });
  }
};
/**
 * Send launch config through to the client window via `postMessage`.
 */
export const sendClientLaunchConfig = (targetWindow, config) => {
  sendPostMessage({
    msgKey: MSG_KEYS.TRANSMIT_CONFIG,
    payload: config,
    targetWindow
  });
};
/**
 * Open a new window, either as a pop-up or in
 * a new tab, in which to load the chat client.
 */
export const openExternalWindow = (url, options) => window.open(url, DOCUMENT_CONTENT.DEFAULT_DOCUMENT_TITLE, WINDOW_OPTIONS(screen, options));